'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertTriangle, Mail, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
  verifyError?: string | null;
}

export default function LoginForm({ onSwitchToRegister, verifyError }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [incompleteSetup, setIncompleteSetup] = useState<{ step: string; message: string } | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resend state for the verify-error banner (user not yet signed in)
  const [linkErrorResendEmail, setLinkErrorResendEmail] = useState('');
  const [linkErrorResendEmailError, setLinkErrorResendEmailError] = useState('');
  const [isLinkErrorResending, setIsLinkErrorResending] = useState(false);
  const [linkErrorResendCooldown, setLinkErrorResendCooldown] = useState(0);
  const [linkErrorResendSuccess, setLinkErrorResendSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (linkErrorResendCooldown <= 0) return;
    const t = setTimeout(() => setLinkErrorResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [linkErrorResendCooldown]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setIncompleteSetup(null);
    setUnverifiedEmail(null);
    const supabase = createClient();

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          // Email not verified — route to verification pending screen
          // Sign the user out to ensure clean state, then show the pending screen
          await supabase.auth.signOut();
          setUnverifiedEmail(data.email);
          setResendCooldown(0);
          setIsLoading(false);
          return;
        } else if (error.message.includes('Invalid login credentials')) {
          toast.error('Invalid email or password', { description: 'Please check your credentials and try again.' });
        } else {
          toast.error(error.message);
        }
        setIsLoading(false);
        return;
      }

      if (!authData.user) {
        toast.error('Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // Check email verification via Supabase Auth (authoritative source)
      if (!authData.user.email_confirmed_at) {
        // User is signed in but email not confirmed — sign out and show pending screen
        await supabase.auth.signOut();
        setUnverifiedEmail(data.email);
        setResendCooldown(0);
        setIsLoading(false);
        return;
      }

      // Fetch user profile to check status
      let userProfile = await jsnService.getUserProfileById(authData.user.id);

      // ── Auth/Profile mismatch repair ──────────────────────────────────────
      if (!userProfile) {
        const repairResult = await jsnService.repairUserProfile(
          authData.user.id,
          authData.user.email || data.email,
          authData.user.user_metadata?.display_name as string | undefined
        );

        if (repairResult.success) {
          userProfile = await jsnService.getUserProfileById(authData.user.id);
        }

        if (!userProfile) {
          toast.error('Account setup incomplete', {
            description: 'Your account exists but setup was interrupted. Please complete registration or contact support.',
            duration: 6000,
          });
          setIncompleteSetup({
            step: 'profile_missing',
            message: 'Your account exists in our system but your profile is incomplete. Please create a new account to complete setup, or contact support.',
          });
          await supabase.auth.signOut();
          setIsLoading(false);
          return;
        }
      }

      if (userProfile.accountStatus === 'deleted') {
        toast.error('Account not found', { description: 'This account has been deleted and cannot be accessed.' });
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      if (userProfile.accountStatus === 'deactivated') {
        setTimeout(() => { window.location.href = '/deactivated'; }, 300);
        return;
      }

      if (userProfile.role === 'admin') {
        toast.success('Welcome, Admin!', { description: 'Redirecting to Admin Dashboard...' });
        setTimeout(() => { window.location.href = '/admin'; }, 800);
        return;
      }

      if (userProfile.accountStatus === 'suspended') {
        setTimeout(() => { window.location.href = '/suspended'; }, 300);
        return;
      }

      // ── Photo-review gate ──────────────────────────────────────────────
      // Every newly registered account has photo_status = 'pending' until an
      // admin approves the photo. Block access to the full member experience
      // until the photo is approved AND the account is active.
      // Admins bypass this gate. /discover-page renders the existing
      // "Photo Pending Review" UI when photoStatus is pending.
      const memberProfile = await jsnService.getCurrentMemberProfile();

      // ── TEMPORARY RUNTIME LOGGING — remove after root cause confirmed ──
      console.log('[JSN Photo Gate] LoginForm — memberProfile raw value:', memberProfile);
      console.log('[JSN Photo Gate] LoginForm — memberProfile is null?', memberProfile === null);
      console.log('[JSN Photo Gate] LoginForm — photoStatus:', memberProfile?.photoStatus ?? '(memberProfile is null/undefined)');
      console.log('[JSN Photo Gate] LoginForm — userProfile.accountStatus:', userProfile?.accountStatus);
      console.log('[JSN Photo Gate] LoginForm — userProfile.role:', userProfile?.role);
      // ──────────────────────────────────────────────────────────────────

      // FAIL-CLOSED: if memberProfile is null (no row, RLS block, or query failure)
      // treat it the same as a pending/unapproved photo — block access to /home.
      if (!memberProfile || memberProfile.photoStatus !== 'approved' || userProfile.accountStatus !== 'active') {
        // Photo not yet approved — route to the existing pending review screen.
        // This covers photo_status: 'pending', 'rejected', 'none' (no photo uploaded),
        // AND the case where memberProfile is null (row missing or RLS blocked SELECT).
        toast.success('Welcome to JSN!', { description: 'Redirecting you now…' });
        setTimeout(() => { window.location.href = '/discover-page'; }, 800);
        return;
      }
      // ──────────────────────────────────────────────────────────────────

      toast.success('Welcome to JSN!', { description: 'Redirecting to Home...' });
      setTimeout(() => { window.location.href = '/home'; }, 800);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail || isResending || resendCooldown > 0) return;
    setIsResending(true);
    const supabase = createClient();
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://us.jamuhurisingles.com';
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: unverifiedEmail,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    setIsResending(false);
    if (error) {
      toast.error('Unable to send verification email. Please try again.', { description: error.message });
    } else {
      setResendCooldown(60);
      toast.success('Verification email sent. Please check your inbox.', {
        description: "Also check your spam or junk folder if you don't see it.",
      });
    }
  };

  const handleLinkErrorResend = async () => {
    setLinkErrorResendEmailError('');
    const emailVal = linkErrorResendEmail.trim();
    if (!emailVal) {
      setLinkErrorResendEmailError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      setLinkErrorResendEmailError('Please enter a valid email address.');
      return;
    }
    if (isLinkErrorResending || linkErrorResendCooldown > 0) return;
    setIsLinkErrorResending(true);
    const supabase = createClient();
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://us.jamuhurisingles.com';
    // Always respond with success to avoid disclosing whether the email exists
    await supabase.auth.resend({
      type: 'signup',
      email: emailVal,
      options: { emailRedirectTo: `${siteUrl}/auth/callback` },
    });
    setIsLinkErrorResending(false);
    setLinkErrorResendSuccess(true);
    setLinkErrorResendCooldown(60);
  };

  // Verification link error banner (from callback redirect)
  const verifyErrorMessage =
    verifyError === 'link_expired' ? 'Your verification link has expired. Please request a new one below.'
      : verifyError === 'link_used' ? 'This verification link has already been used. If your email is not verified, request a new link below.'
      : verifyError === 'link_invalid' ? 'This verification link is invalid. Please request a new one below.'
      : null;

  return (
    <div className="fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Sign In</h2>
        <p className="text-muted-foreground text-sm mt-1">Sign in to your JSN account</p>
      </div>

      {/* Verification link error banner + resend control */}
      {verifyErrorMessage && !unverifiedEmail && (
        <div className="space-y-3">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex gap-3">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">Verification Link Issue</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{verifyErrorMessage}</p>
            </div>
          </div>

          {/* Resend control for expired/invalid link */}
          <div className="bg-muted/20 border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-primary flex-shrink-0" />
              <p className="text-sm font-semibold text-foreground">Resend Verification Email</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Enter your email address to receive a new verification link. You do not need to sign in first.
            </p>

            {linkErrorResendSuccess ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-start gap-2">
                <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-green-600 dark:text-green-400">Verification email sent</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    If that address has an unverified JSN account, a new link is on its way. Check your inbox and spam/junk folder.
                  </p>
                  {linkErrorResendCooldown > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Resend again in <span className="font-semibold">{linkErrorResendCooldown}s</span>
                    </p>
                  )}
                  {linkErrorResendCooldown === 0 && (
                    <button
                      onClick={() => { setLinkErrorResendSuccess(false); setLinkErrorResendEmail(''); }}
                      className="text-xs text-primary hover:text-primary-light font-semibold transition-colors mt-1"
                    >
                      Send to a different address
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div>
                  <input
                    type="email"
                    className="input-jsn"
                    placeholder="your@email.com"
                    autoComplete="email"
                    value={linkErrorResendEmail}
                    onChange={e => { setLinkErrorResendEmail(e.target.value); setLinkErrorResendEmailError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleLinkErrorResend(); } }}
                  />
                  {linkErrorResendEmailError && (
                    <p className="text-primary text-xs mt-1">{linkErrorResendEmailError}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleLinkErrorResend}
                  disabled={isLinkErrorResending || linkErrorResendCooldown > 0}
                  className="w-full py-2.5 rounded-xl text-xs font-semibold border border-primary/30 text-primary hover:bg-primary/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLinkErrorResending ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <RefreshCw size={13} />
                  )}
                  {linkErrorResendCooldown > 0
                    ? `Resend available in ${linkErrorResendCooldown}s`
                    : isLinkErrorResending
                    ? 'Sending...' :'Send Verification Email'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Email verification pending — blocking gate */}
      {unverifiedEmail && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Mail size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Email Verification Required</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                You must verify your email address before accessing JSN.
                A verification link was sent to{' '}
                <span className="font-semibold text-foreground">{unverifiedEmail}</span>.
              </p>
              <p className="text-xs text-muted-foreground">
                Don&apos;t see it? Check your <strong>Spam</strong>, <strong>Junk</strong>, or <strong>Promotions</strong> folder.
              </p>
            </div>
          </div>
          <button
            onClick={handleResendVerification}
            disabled={isResending || resendCooldown > 0}
            className="w-full py-2.5 rounded-xl text-xs font-semibold border border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isResending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : isResending
              ? 'Sending...' :'Resend Verification Email'}
          </button>
          <button
            onClick={() => setUnverifiedEmail(null)}
            className="w-full py-2 rounded-xl text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      )}

      {incompleteSetup && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Account Setup Incomplete</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{incompleteSetup.message}</p>
            <button
              onClick={onSwitchToRegister}
              className="text-xs text-primary hover:text-primary-light font-semibold transition-colors mt-1"
            >
              Complete Registration →
            </button>
          </div>
        </div>
      )}

      {!unverifiedEmail && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <label className="label-jsn" htmlFor="login-email">Email Address</label>
            <input
              id="login-email"
              type="email"
              className="input-jsn"
              placeholder="your@email.com"
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
            />
            {errors.email && <p className="text-primary text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-jsn" htmlFor="login-password" style={{ marginBottom: 0 }}>Password</label>
              <a href="/forgot-password" className="text-xs text-primary hover:text-primary-light transition-colors">
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className="input-jsn pr-10"
                placeholder="Your password"
                autoComplete="current-password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-primary text-xs mt-1">{errors.password.message}</p>}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember-me"
              type="checkbox"
              className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              {...register('rememberMe')}
            />
            <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            {isLoading ? (
              <><Loader2 size={16} className="animate-spin" />Signing in...</>
            ) : (
              'Sign In to JSN'
            )}
          </button>
        </form>
      )}

      {!unverifiedEmail && (
        <>
          <div className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <button onClick={onSwitchToRegister} className="text-primary hover:text-primary-light font-semibold transition-colors">
              Create Account
            </button>
          </div>

          <div className="text-center">
            <a href="/help" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              Need help? View FAQ
            </a>
          </div>

          <div className="border border-border rounded-xl p-4 bg-muted/20 space-y-2">
            <p className="text-sm font-semibold text-foreground">Returning to JSN?</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Sign in to continue browsing profiles, checking matches, and reading your messages.
            </p>
            <div className="space-y-1 pt-1">
              <p className="text-xs text-muted-foreground">
                Need help signing in?{' '}
                <a href="/help" className="text-primary hover:text-primary-light font-medium transition-colors">
                  View FAQ
                </a>
              </p>
              <p className="text-xs text-muted-foreground">
                Forgot password? Use the reset link above.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}